import { Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';

import { fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { IPackagingProductListItem } from 'src/types/packaging-product';

import { useBoolean } from '../../hooks/use-boolean';

type Props = {
  row: IPackagingProductListItem;
  onEditRow: VoidFunction;
  onDeleteRow: VoidFunction;
};

export default function PackagingProductTableRow({ row, onEditRow, onDeleteRow }: Props) {
  const { productName, productCode, status, updatedAt } = row;

  const { user: authUser } = useAuthContext();

  const confirm = useBoolean();

  const popover = usePopover();

  return (
    <>
      <TableRow hover>
        <TableCell align="left">{productName}</TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>{productCode || '-'}</TableCell>

        <TableCell>
          <Label variant="soft" color={status === 'active' ? 'success' : 'default'}>
            {status}
          </Label>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
          {fDateTime(updatedAt)}
        </TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          {hasPermission(
            authUser,
            'any',
            AuthPermissions.UPDATE_PRODUCT,
            AuthPermissions.DELETE_PRODUCT,
            AuthPermissions.ADMIN_PERMISSION,
            AuthPermissions.SUPER_ADMIN_PERMISSION
          ) && (
            <Tooltip title="More options">
              <IconButton
                color={popover.open ? 'inherit' : 'default'}
                onClick={(e) => {
                  popover.onOpen(e);
                }}
              >
                <Iconify icon="eva:more-vertical-fill" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 180 }}
      >
        {hasPermission(
          authUser,
          'any',
          AuthPermissions.UPDATE_PRODUCT,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <MenuItem
            onClick={() => {
              onEditRow();
              popover.onClose();
            }}
          >
            <Iconify icon="solar:pen-bold" />
            Edit
          </MenuItem>
        )}

        {hasPermission(
          authUser,
          'any',
          AuthPermissions.DELETE_PRODUCT,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) &&
          status === 'active' && (
            <MenuItem
              onClick={() => {
                confirm.onTrue();
                popover.onClose();
              }}
              sx={{ color: 'error.main' }}
            >
              <Iconify icon="solar:trash-bin-trash-bold" />
              Deactivate
            </MenuItem>
          )}
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Deactivate Product"
        content={`Are you sure you want to deactivate ${productName}? It will no longer be selectable for new dispatches, but existing records stay intact.`}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              confirm.onFalse();
              onDeleteRow();
            }}
          >
            Deactivate
          </Button>
        }
      />
    </>
  );
}
